import { Component, inject, OnInit, OnDestroy, signal, ChangeDetectorRef, Input, NgZone } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

import { AlertPrioritySchedulePayload, SiteService } from 'src/app/services/site.service';
import { UtilService } from 'src/app/services/util.service';
import { AlertService } from 'src/app/services/alert.service';
import { RtspCameraService } from 'src/app/services/rtsp-camera.service';
import { UserManualService } from 'src/app/services/user-manual.service';

import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { DatePickerModule } from 'primeng/datepicker';
import { SwSelectComponent } from 'src/app/shared/components/sw-select/sw-select.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import momentTz from 'moment-timezone';
import { localStorageKeys, userRoleNameEnum } from 'src/app/shared/const/enum';
import { DEFAULT_LOCATION_TEXT_COLOR } from 'src/app/shared/const/camera-location';
import {
  ALERT_PRIORITY_OPTIONS,
  AlertPriority,
  getAlertPriorityIcon,
  toAlertPriority
} from 'src/app/shared/const/alert-priority';
import {
  PriorityScheduleRow,
  crossesMidnight,
  dateToWindowTime,
  emptyPriorityScheduleRow,
  formatWindowRange,
  priorityScheduleRowError,
  toPriorityScheduleRow,
  toPrioritySchedulePayload,
  windowOverlapError,
  windowTimeToDate
} from 'src/app/shared/const/alert-priority-schedule';
import {
  DEFAULT_DISARM_TIME_OPTIONS,
  DisarmTimeOption,
  MAX_DISARM_HOURS,
  MAX_DISARM_MINUTES,
  disarmTimeToMinutes,
  findDisarmOption,
  formatDisarmMinutes,
  normalizeDisarmOptions
} from 'src/app/shared/const/disarm-time';

@Component({
  selector: 'app-camera-model',
  templateUrl: './camera-model.component.html',
  styleUrls: ['./camera-model.component.scss'],
  imports: [
    SwSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbTooltip,
    DatePickerModule
  ]
})
export class CameraModelComponent implements OnInit, OnDestroy {

  // Rendered inside the shared <sw-drawer> (app.component). When embedded the
  // panel hides its own header/chrome and closes via SiteService instead of the
  // ngx-bootstrap modal stack.
  @Input() embedded: boolean = false;

  private modalService = inject(BsModalService);
  private siteService = inject(SiteService);
  private utilService = inject(UtilService);
  private alertService = inject(AlertService);
  private rtspCameraService = inject(RtspCameraService);
  private router = inject(Router);
  protected manual = inject(UserManualService);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  camera = [];
  isLoading: boolean = false;
  selectedTime: string;
  allSelectToggle: boolean;
  falseFlag: boolean;
  isShowOnlyCamera: boolean = false;
  cameraSiteID: string;
  userId: string;
  ID: string;
  isAnythingChanged: boolean = true;
  is_enable: boolean

  loading = signal(false);

  // Camera Location Dropdown Properties
  cameraLocationOptions: any[] = [];
  selectedCameraLocation: string = '';

  canManageDropdown: boolean = false;
  // Rows 96/97 of the feature matrix: Edit Camera (rename) and Create Zone stay
  // open to Admin/IT/Manager but drop Operator ("Remove Operator").
  canEditCameraOrZone: boolean = false;
  loginUserRole: string = '';

  // Site-level object detection (shown at top of the camera panel)
  siteData: any;
  siteAlertActive: boolean = false;
  siteFaceDetection: boolean = false;
  siteVehicleDetection: boolean = false;
  siteZoneDetection: boolean = false;
  siteRtspRecording: boolean = true;

  // ---- Alert priority lists, above the camera list ----
  //
  // The three lists ARE the assignment: a camera sits in exactly one of them, and each
  // list only offers cameras the other two do not hold. This is the only place a camera's
  // priority is set - the card below carries no control of its own, so there is one place
  // to read it and one place to change it.
  //
  // A list only offers cameras the other two are not holding, so one camera can never be
  // claimed by two levels. Cameras with no priority at all — the default — are offered by
  // all three, which is how a camera gets into one in the first place. Taking a chip out
  // clears that camera's level, so it goes back to having none.
  priorityBuckets: Record<string, string[]> = { high: [], medium: [], low: [] };
  priorityCameraOptions: Record<string, any[]> = { high: [], medium: [], low: [] };
  isApplyingPriority = false;

  // What the server is known to hold, so a selectionChange can be diffed into "added here"
  // without re-reading the cameras.
  private bucketSnapshot: Record<string, string[]> = { high: [], medium: [], low: [] };

  // ---- Time-based priority: one recurring window per level ----
  //
  // A window overrides its level for the hours it covers and only those hours: outside it
  // an alert is still stamped from its camera's own level, so a site with no windows
  // behaves exactly as it did before the feature existed.
  //
  // Hung off the three priority rows rather than listed separately, because the level is
  // not a field the operator picks — it is the row the window belongs to. Collapsed to
  // "8:00 PM → 7:00 AM" once saved; the editor is only open for the one level being
  // changed, which is what `windowEditorLevel` holds.
  priorityWindows: Record<string, PriorityScheduleRow | null> = { high: null, medium: null, low: null };
  windowEditorLevel: AlertPriority | null = null;
  windowDraft: PriorityScheduleRow | null = null;
  windowDraftError: string | null = null;
  // PrimeNG's time picker binds a Date; the row stores a time of day. These are the two
  // ends of that conversion, applied on open and read back on save rather than mapped on
  // every keystroke — a two-way pipe through a getter re-created the Date each change
  // detection pass and closed the picker's own panel while it was being used.
  windowDraftStart: Date | null = null;
  windowDraftEnd: Date | null = null;
  priorityScheduleLoading = false;
  priorityScheduleSaving = false;

  // High | Medium | Low. Unlike the detection flags there is no site-level master
  // switch — a camera owns its priority outright.
  readonly priorityOptions = ALERT_PRIORITY_OPTIONS;

  // Quick disarm intervals: the same list the operator console's Camera Disarm popup
  // offers — defaults plus whatever has been added through /camera/addCameraDisarmMinutes.
  disarmTimeOptions: DisarmTimeOption[] = [...DEFAULT_DISARM_TIME_OPTIONS];
  readonly maxDisarmHours = MAX_DISARM_HOURS;
  readonly maxDisarmMinutes = MAX_DISARM_MINUTES;
  showIntervalCustomTime = false;
  customIntervalHours = 0;
  customIntervalMinutes = 0;
  isSavingCustomInterval = false;
  isApplyingIntervalToAll = false;
  // The camera whose inline interval picker is open, and the interval it holds.
  quickDisarmCameraId: string | null = null;
  quickDisarmTime = '';
  isStoppingSiteDisarm = false;

  // Countdown clock. A disarm's expiry is not stored — the API keeps the moment it
  // started (`is_interval_active_time`) and the duration text (`interval_time`), so the
  // remaining time is recomputed here every second. The ticker only runs while some
  // camera is actually disarmed.
  nowMs = Date.now();
  private countdownRef: any = null;

  hours = [
    {
      time: '1 hours'
    },
    {
      time: '2 hours'
    },
    {
      time: '3 hours'
    },
    {
      time: '4 hours'
    },
    {
      time: '5 hours'
    },
    {
      time: '6 hours'
    },
    // {
    //   time: '7 hours'
    // },
    // {
    //   time: '8 hours'
    // },
    // {
    //   time: '9 hours'
    // },
    // {
    //   time: '10 hours'
    // },
    // {
    //   time: '11 hours'
    // },
    // {
    //   time: '12 hours'
    // },
  ];

  constructor() {
    this.userId = this.utilService.getLocalStorage('usersId');
  }

  ngOnInit(): void {
    const camera = cloneDeep(this.siteService.cameraList);
    this.isShowOnlyCamera = cloneDeep(this.siteService.cameraModelType);
    camera.map((camera) => {
      camera.isShowEditMode = false;
      camera.selectedCameraLocation = '';
      // Camera detection flags default to true — only an explicit `false`
      // turns a camera off, so undefined/missing is treated as enabled.
      camera.is_face_detection = camera.is_face_detection !== false;
      camera.is_vehicle_detection = camera.is_vehicle_detection !== false;
      camera.is_zone_detection = camera.is_zone_detection !== false;
      camera.is_alert_active = camera.is_alert_active !== false;
      camera.is_rtsp_recording = camera.is_rtsp_recording === true;
      if (camera.user_disarm !== undefined) {
        camera.is_interval_active = !!camera.user_disarm?.is_interval_active;
        camera.interval_time = camera.user_disarm?.interval_time || camera.interval_time;
        camera.is_interval_active_time = camera.user_disarm?.is_interval_active_time || null;
      }
      // Strict: a camera nobody has set a priority on carries null, and the segmented
      // control shows no tile selected. Defaulting it here would draw "Low" as though
      // someone had chosen it.
      camera.alert_priority = toAlertPriority(camera.alert_priority);
    });
    this.camera = camera;
    this.rebuildPriorityBuckets();
    if (this.camera && this.camera.length && this.camera.length > 0 && this.camera[0].interval_time) {
      this.selectedTime = this.camera[0].interval_time;
    } else {
      this.selectedTime = this.hours[0].time;
    }
    this.falseFlag = this.siteService.flag;
    this.cameraSiteID = this.siteService.cameraSiteID;
    this.siteData = this.siteService.cameraSiteData;
    // Consume once so other callers that show this modal directly
    // (speaker / grouped-sites / setting-modal) don't inherit stale site data.
    this.siteService.cameraSiteData = null;
    if (this.siteData) {
      this.siteAlertActive = this.siteData.is_alert_active !== false;
      this.siteFaceDetection = !!this.siteData.is_face_detection;
      this.siteVehicleDetection = !!this.siteData.is_vehicle_detection;
      this.siteZoneDetection = !!this.siteData.is_zone_detection;
      this.siteRtspRecording = this.siteData.is_rtsp_recording === true;
    }
    let checkIfAllChecked = this.camera.filter((obj) => obj.is_interval_active == false);
    if (checkIfAllChecked && checkIfAllChecked.length > 0) {
      this.allSelectToggle = false;
    } else {
      this.allSelectToggle = true;
    }

    // The main drawer's interval select runs off the shared disarm list; the legacy
    // quick-disarm mode (falseFlag) keeps its own `hours` options untouched.
    if (!this.falseFlag) {
      this.selectedTime = this.resolveOptionValue(this.selectedTime);
      this.loadDisarmTimeOptions();
    }

    // Same gate as the priority block in the template: the legacy quick-disarm mode and
    // the read-only camera list neither show the windows nor need to pay for the request.
    if (!this.falseFlag && !this.isShowOnlyCamera) {
      this.loadPriorityScheduleRows();
    }

    // Initialize dropdown functionality
    this.checkUserPermissions();
    this.fetchDropdownOptions();
    this.syncCountdown();
    this.emitDisarmSummary();
  }

  /**
   * Re-arm the whole site: every camera still holding a disarm goes back on, the same
   * way the pill re-arms one. The counterpart to "Disarm All" — without it a site
   * disarmed for six hours could only be recovered camera by camera.
   */
  stopSiteDisarm() {
    const disarmed = this.camera.filter((c) => c.is_interval_active);
    if (!disarmed.length || this.isStoppingSiteDisarm) {
      return;
    }
    const params = disarmed.map((c) => ({
      // The API rejects an empty interval even when re-arming, so the camera's own
      // interval is sent back untouched.
      interval_time: c.interval_time || this.selectedTime || this.hours[0].time,
      camera_id: c._id,
      is_interval_active: false,
    }));
    this.isStoppingSiteDisarm = true;
    const a: Subscription = this.siteService.updateIntervalTime(params).subscribe({
      next: (res) => {
        this.isStoppingSiteDisarm = false;
        if (!res) {
          this.utilService.showErrorInfo('Something wrong!');
          return;
        }
        disarmed.forEach((c) => (c.is_interval_active = false));
        this.allSelectToggle = false;
        this.quickDisarmCameraId = null;
        this.utilService.showErrorSuccess('All cameras armed again.');
        this.syncCountdown();
        this.emitDisarmSummary();
        this.cdr.detectChanges();
      },
      error: (e) => { this.isStoppingSiteDisarm = false; console.log(e); },
      complete: () => a.unsubscribe()
    });
  }

  // ---- Disarm countdown ----

  /** When a camera's disarm runs out, in epoch ms — null when it is not disarmed. */
  private disarmEndsAt(item: any): number | null {
    if (!item?.is_interval_active || !item.is_interval_active_time) {
      return null;
    }
    const minutes = disarmTimeToMinutes(item.interval_time);
    if (isNaN(minutes)) {
      return null;
    }
    const start = momentTz(item.is_interval_active_time).tz('America/New_York');
    if (!start.isValid()) {
      return null;
    }
    return start.clone().add(minutes, 'minutes').valueOf();
  }

  /** Milliseconds left on a camera's disarm; 0 once it has run out. */
  cameraRemainingMs(item: any): number {
    const endsAt = this.disarmEndsAt(item);
    return endsAt === null ? 0 : Math.max(0, endsAt - this.nowMs);
  }

  cameraCountdown(item: any): string {
    return this.formatCountdown(this.cameraRemainingMs(item));
  }

  get disarmedCameras(): any[] {
    return this.camera.filter((c) => c.is_interval_active);
  }

  /**
   * The site is only fully armed again when the LAST camera's disarm runs out, so the
   * site-level countdown reads the furthest expiry rather than the nearest.
   */
  siteRemainingMs(): number {
    const ends = this.disarmedCameras
      .map((c) => this.disarmEndsAt(c))
      .filter((end): end is number => end !== null);
    if (!ends.length) {
      return 0;
    }
    return Math.max(0, Math.max(...ends) - this.nowMs);
  }

  siteCountdown(): string {
    return this.formatCountdown(this.siteRemainingMs());
  }

  /**
   * Tell the sites list what this site now looks like. Computed from the cameras already in
   * hand, so changing a couple of cameras here costs no request at all — the list's Quick
   * Disarm column would otherwise keep showing the count from its last /sites/list.
   *
   * Same rule the API uses: a camera counts while its disarm has not run out, and one with
   * no interval never runs out.
   */
  private emitDisarmSummary() {
    if (!this.cameraSiteID || typeof this.cameraSiteID !== 'string') {
      return;
    }
    const now = Date.now();
    const disarmed = this.camera.filter((c) => {
      if (!c.is_interval_active) {
        return false;
      }
      const endsAt = this.disarmEndsAt(c);
      return endsAt === null || endsAt > now;
    });
    const ends = disarmed.map((c) => this.disarmEndsAt(c));
    // One open-ended disarm and the site has no end time at all.
    const endsAt = ends.some((end) => end === null)
      ? null
      : (ends.length ? new Date(Math.max(...(ends as number[]))).toISOString() : null);
    this.siteService.siteDisarmSummary$.next({
      siteId: this.cameraSiteID,
      disarmed_cameras: disarmed.length,
      ends_at: endsAt,
    });
  }

  /** A disarm the clock has passed, still flagged on the camera — see `syncCountdown`. */
  isDisarmExpired(item: any): boolean {
    return !!item?.is_interval_active && this.cameraRemainingMs(item) <= 0;
  }

  get isSiteDisarmExpired(): boolean {
    return this.disarmedCameras.length > 0 && this.siteRemainingMs() <= 0;
  }

  private formatCountdown(ms: number): string {
    if (ms <= 0) {
      return '0s';
    }
    const total = Math.floor(ms / 1000);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    if (hours > 0) {
      return `${hours}h ${pad(minutes)}m ${pad(seconds)}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${pad(seconds)}s`;
    }
    return `${seconds}s`;
  }

  /**
   * Start ticking while something is disarmed, stop when nothing is. Outside the zone so
   * a six-hour drawer does not run an app-wide change detection pass every second — the
   * countdown paints itself with a local detectChanges.
   *
   * A disarm whose time has run out is shown as armed straight away: alert routing reads
   * `isIntervalStillActive`, which already treats it as armed, so there is nothing left to
   * stop. The stored flag is left as it is — no request is made for it.
   */
  private armExpiredDisarms(): boolean {
    const now = Date.now();
    const expired = this.camera.filter((c) => {
      const endsAt = c.is_interval_active ? this.disarmEndsAt(c) : null;
      return endsAt !== null && endsAt <= now;
    });
    if (!expired.length) {
      return false;
    }
    expired.forEach((c) => (c.is_interval_active = false));
    this.allSelectToggle = this.camera.every((c) => c.is_interval_active);
    return true;
  }

  private syncCountdown() {
    this.armExpiredDisarms();
    const shouldRun = this.camera.some((c) => c.is_interval_active);
    if (shouldRun && !this.countdownRef) {
      this.zone.runOutsideAngular(() => {
        this.countdownRef = setInterval(() => {
          this.nowMs = Date.now();
          if (this.armExpiredDisarms()) {
            this.emitDisarmSummary();
            this.syncCountdown();
          }
          this.cdr.detectChanges();
        }, 1000);
      });
      return;
    }
    if (!shouldRun && this.countdownRef) {
      clearInterval(this.countdownRef);
      this.countdownRef = null;
    }
  }

  ngOnDestroy(): void {
    if (this.countdownRef) {
      clearInterval(this.countdownRef);
      this.countdownRef = null;
    }
  }

  isModelSchedule() {
    return UtilService.activeCameraModelType == 1 ? true : false;
  }

  saveCameraNewName(item: any, index: number) {
    const params = {
      camera_id: item._id,
      camera_name: item.display_name
    }
    const a: Subscription = this.siteService.updateCameraName(params).subscribe({
      next: (res) => {
        if (res) {
          this.utilService.showErrorSuccess(res.message);
        } else {
          this.utilService.showErrorInfo('Something wrong!');
        }
        this.camera[index].isShowEditMode = false;
      }, error: (e) => { this.isLoading = false; console.log(e) },
      complete: () => a.unsubscribe()
    })
  }

  clickCamera(id: string) {
    if (!this.falseFlag && !this.isShowOnlyCamera) {
      this.closeModal();
      localStorage.setItem('siteid', this.cameraSiteID);
      this.router.navigate([`/users/${this.userId}/alerts/${this.cameraSiteID}`], { queryParams: { camera_id: id } });
    }
  }

  selectFn(e) {
    this.isAnythingChanged = false;
    this.camera.map((obj) => {
      obj.is_interval_active = e.target.checked;
      return obj;
    });
  }

  changeToggleItem() {
    this.allSelectToggle = this.camera.every((obj) => obj.is_interval_active);
  }

  changeThirdPartyToggle(item) {
    const a: Subscription = this.siteService.changeToggleThirdParty(item._id, { third_party_URL_call: item.third_party_URL_call }).subscribe({
      next: (res) => {
        if (res) {
          this.utilService.showErrorSuccess(res.message);
        } else {
          this.utilService.showErrorInfo('Something wrong!');
        }
      }, error: (e) => { this.loading.set(false); this.isLoading = false; console.log(e) },
      complete: () => a.unsubscribe()
    })
  }

  changeCameraStatus(item, prev?: boolean) {
    const updateSiteStatus = this.siteService.cameraEnableDisable(item._id, { is_alert_active: item.is_alert_active }).subscribe({
      next: () => {
        // Success toast intentionally suppressed for toggle switches.
      },
      error: (e) => {
        // revert optimistic update on failure
        if (prev !== undefined) {
          item.is_alert_active = prev;
        }
        console.log(e);
      },
      complete: () => updateSiteStatus.unsubscribe()
    });
  }

  // Per-camera quick disarm (main camera drawer). Persists live — mirrors the other
  // per-camera live toggles here. `intervalTime` is what the camera was disarmed for;
  // without one it falls back to the common interval at the top of the panel.
  private persistIntervals(cameras: any[], intervalTime?: string) {
    if (!cameras.length) {
      return;
    }
    const params = cameras.map((c) => ({
      interval_time: intervalTime || this.selectedTime || this.hours[0].time,
      camera_id: c._id,
      is_interval_active: !!c.is_interval_active,
    }));
    const a: Subscription = this.siteService.updateIntervalTime(params).subscribe({
      // Success toast intentionally suppressed for toggle switches.
      next: () => { },
      error: (e) => console.log(e),
      complete: () => a.unsubscribe()
    });
  }

  // Disarming asks for how long first — the pill opens an inline interval picker on
  // that card. Re-arming needs no interval, so it stays a straight toggle.
  toggleCameraQuickDisarm(item: any) {
    if (item.is_interval_active) {
      item.is_interval_active = false;
      this.quickDisarmCameraId = null;
      this.persistIntervals([item], item.interval_time);
      this.changeToggleItem();
      this.syncCountdown();
      this.emitDisarmSummary();
      return;
    }
    if (this.quickDisarmCameraId === item._id) {
      this.quickDisarmCameraId = null;
      return;
    }
    this.quickDisarmCameraId = item._id;
    this.quickDisarmTime = this.resolveOptionValue(item.interval_time || this.selectedTime);
  }

  cancelCameraQuickDisarm() {
    this.quickDisarmCameraId = null;
  }

  confirmCameraQuickDisarm(item: any) {
    if (!this.quickDisarmTime) {
      return;
    }
    item.is_interval_active = true;
    item.interval_time = this.quickDisarmTime;
    // The server stamps the same moment; mirroring it here starts the countdown now
    // instead of on the next time the drawer is opened.
    item.is_interval_active_time = momentTz().tz('America/New_York').toISOString();
    this.persistIntervals([item], this.quickDisarmTime);
    this.quickDisarmCameraId = null;
    this.changeToggleItem();
    this.syncCountdown();
    this.emitDisarmSummary();
  }

  /**
   * Site-wide quick disarm: every camera in this site goes disarmed for the selected
   * interval. Deliberate enough to need its own button — firing it on the select's
   * change would silently mute a whole site's alerts on a mis-click.
   */
  applyIntervalToAllCameras() {
    if (!this.selectedTime || this.isApplyingIntervalToAll || !this.camera.length) {
      return;
    }
    const intervalTime = this.selectedTime;
    const params = this.camera.map((c) => ({
      interval_time: intervalTime,
      camera_id: c._id,
      is_interval_active: true,
    }));
    this.isApplyingIntervalToAll = true;
    const a: Subscription = this.siteService.updateIntervalTime(params).subscribe({
      next: (res) => {
        this.isApplyingIntervalToAll = false;
        if (!res) {
          this.utilService.showErrorInfo('Something wrong!');
          return;
        }
        const startedAt = momentTz().tz('America/New_York').toISOString();
        this.camera.forEach((c) => {
          c.is_interval_active = true;
          c.interval_time = intervalTime;
          c.is_interval_active_time = startedAt;
        });
        this.allSelectToggle = true;
        this.quickDisarmCameraId = null;
        this.utilService.showErrorSuccess(`All cameras quick disarmed for ${intervalTime}.`);
        this.syncCountdown();
        this.emitDisarmSummary();
        this.cdr.detectChanges();
      },
      error: (e) => { this.isApplyingIntervalToAll = false; console.log(e); },
      complete: () => a.unsubscribe()
    });
  }

  // ---- Quick disarm interval list ----

  /** Merge the custom intervals added through the console into the default list. */
  private loadDisarmTimeOptions() {
    const a: Subscription = this.siteService.getCameraDisarmMinutes().subscribe({
      next: (res) => {
        const list = res?.status && Array.isArray(res.list) ? res.list : [];
        const custom: DisarmTimeOption[] = list
          .filter((entry: any) => entry?.camera_disarm_time)
          .map((entry: any) => ({
            name: formatDisarmMinutes(disarmTimeToMinutes(entry.camera_disarm_time)),
            value: entry.camera_disarm_time,
          }));
        this.disarmTimeOptions = normalizeDisarmOptions([...DEFAULT_DISARM_TIME_OPTIONS, ...custom]);
        this.selectedTime = this.resolveOptionValue(this.selectedTime);
        this.cdr.detectChanges();
      },
      error: (e) => console.log(e),
      complete: () => a.unsubscribe()
    });
  }

  /**
   * The option carrying the same duration as `value`, adding it to the list when the
   * saved interval is not offered. Cameras store free text ("1 hours") while the list
   * holds "1 hour", and an unmatched value leaves the select showing nothing.
   */
  private resolveOptionValue(value: string): string {
    if (!value) {
      return this.disarmTimeOptions[0]?.value || '';
    }
    const match = findDisarmOption(this.disarmTimeOptions, value);
    if (match) {
      return match.value;
    }
    const total = disarmTimeToMinutes(value);
    this.disarmTimeOptions = normalizeDisarmOptions([
      ...this.disarmTimeOptions,
      { name: isNaN(total) ? value : formatDisarmMinutes(total), value },
    ]);
    return value;
  }

  toggleIntervalCustomTime() {
    this.showIntervalCustomTime = !this.showIntervalCustomTime;
    if (!this.showIntervalCustomTime) {
      this.customIntervalHours = 0;
      this.customIntervalMinutes = 0;
    }
  }

  /** Validation for the hours/minutes pair, or null when it is usable. */
  get customIntervalError(): string | null {
    const hours = Number(this.customIntervalHours);
    const minutes = Number(this.customIntervalMinutes);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours < 0 || minutes < 0) {
      return 'Enter a valid time.';
    }
    if (minutes > 59) {
      return 'Minutes must be between 0 and 59.';
    }
    const total = (hours * 60) + minutes;
    if (total <= 0) {
      return 'Enter a disarm time greater than 0 minutes.';
    }
    if (total > this.maxDisarmMinutes) {
      return `Camera disarm time cannot exceed ${this.maxDisarmHours} hours.`;
    }
    return null;
  }

  /** Add a custom interval to the shared list, then select it. */
  saveCustomInterval() {
    // The number inputs still accept typed/pasted values past their max, so the limit
    // is enforced here rather than trusting the attributes alone.
    const error = this.customIntervalError;
    if (error) {
      this.utilService.showErrorInfo(error);
      return;
    }
    const totalMinutes = (Number(this.customIntervalHours) * 60) + Number(this.customIntervalMinutes);
    const customTime = `${totalMinutes} minutes`;
    this.isSavingCustomInterval = true;
    const a: Subscription = this.siteService.addCameraDisarmMinutes({
      name: customTime,
      camera_disarm_time: customTime,
      is_hours: true,
    }).subscribe({
      next: (res) => {
        this.isSavingCustomInterval = false;
        if (!res) {
          this.utilService.showErrorInfo('Something wrong!');
          return;
        }
        this.utilService.showErrorSuccess('Camera disarm minutes added successfully.');
        this.disarmTimeOptions = normalizeDisarmOptions([
          ...this.disarmTimeOptions,
          { name: formatDisarmMinutes(totalMinutes), value: customTime },
        ]);
        this.selectedTime = customTime;
        this.showIntervalCustomTime = false;
        this.customIntervalHours = 0;
        this.customIntervalMinutes = 0;
        this.cdr.detectChanges();
      },
      error: (e) => { this.isSavingCustomInterval = false; console.log(e); },
      complete: () => a.unsubscribe()
    });
  }

  saveCameraModel() {
    this.loading.set(true);
    this.isAnythingChanged = false;
    const params = [];
    this.camera.map((obj) => {
      params.push({
        interval_time: this.selectedTime,
        camera_id: obj._id,
        is_interval_active: obj.is_interval_active,
      })
    });

    const a: Subscription = this.siteService.updateIntervalTime(params).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res) {
          this.utilService.showErrorSuccess(res.message);
          if (this.embedded) {
            this.siteService.closeCameraDrawer('Changes made');
          } else {
            this.modalService.hide();
          }
        } else {
          this.utilService.showErrorInfo('Something wrong!');
        }
      }, error: (e) => { this.loading.set(false); this.isLoading = false; console.log(e) },
      complete: () => a.unsubscribe()
    })
  }

  // Site-level alert active. Persists live and keeps the site row (same object
  // reference as the list row) in sync so the list reflects it on drawer close.
  toggleSiteAlertActive() {
    if (!this.siteData?._id) {
      return;
    }
    const prev = this.siteAlertActive;
    this.siteAlertActive = !this.siteAlertActive;
    const a: Subscription = this.siteService
      .siteEnableDisable(this.siteData._id, { is_alert_active: this.siteAlertActive })
      .subscribe({
        next: () => {
          // Success toast intentionally suppressed for toggle switches.
          this.siteData.is_alert_active = this.siteAlertActive;
          this.isAnythingChanged = false;
        },
        error: (e) => {
          this.siteAlertActive = prev;
          this.utilService.showErrorMessage('Something went wrong. please try again.');
          console.log(e);
        },
        complete: () => a.unsubscribe()
      });
  }

  updateSiteObjectDetection() {
    if (!this.siteData?._id) {
      return;
    }
    const payload = {
      site: this.siteData._id,
      is_face_detection: this.siteFaceDetection ? true : false,
      is_vehicle_detection: this.siteVehicleDetection ? true : false
    };
    const a: Subscription = this.siteService.changeSiteFaceDetection(payload).subscribe({
      next: (res) => {
        if (res && res.status) {
          // Success toast intentionally suppressed for toggle switches.
          this.siteData.is_face_detection = this.siteFaceDetection;
          this.siteData.is_vehicle_detection = this.siteVehicleDetection;
        } else {
          this.utilService.showErrorMessage('Something went wrong. please try again.');
        }
      },
      error: (e) => { console.log(e); },
      complete: () => a.unsubscribe()
    });
  }

  toggleFaceDetection() {
    this.siteFaceDetection = !this.siteFaceDetection;
    this.updateSiteObjectDetection();
  }

  toggleVehicleDetection() {
    this.siteVehicleDetection = !this.siteVehicleDetection;
    this.updateSiteObjectDetection();
  }

  getZoneActiveMessage() {
    return `First, you must save the object detection method. After that, create a zone. Detection will work only when the Zone Active button is enabled; otherwise, it will not work.`;
  }

  toggleZoneDetection() {
    if (!this.siteData?._id) {
      return;
    }
    this.siteZoneDetection = !this.siteZoneDetection;
    const a: Subscription = this.siteService
      .zoneActiveStatusChange(this.siteData._id, { is_zone_detection: this.siteZoneDetection })
      .subscribe({
        next: () => {
          // Success toast intentionally suppressed for toggle switches.
          this.siteData.is_zone_detection = this.siteZoneDetection;
        },
        error: (e) => console.log(e),
        complete: () => a.unsubscribe()
      });
  }

  // ---- RTSP recording (site master switch + per-camera override) ----
  // The media service records a camera only when both are on, so the camera
  // pills go dead while the site switch is off — same shape as detection.
  // A camera can be recorded only when it is linked to a Live URL (live_url_index).
  hasLiveUrl(item: any): boolean {
    return item?.live_url_index != null;
  }

  // The site switch is meaningless until at least one camera has a stream. Turning it OFF
  // stays possible so a site that was switched on earlier is never stuck.
  get siteRtspUnavailable(): boolean {
    return !this.siteRtspRecording && !this.camera.some((camera) => this.hasLiveUrl(camera));
  }

  toggleSiteRtspRecording() {
    if (!this.siteData?._id || this.siteRtspUnavailable) {
      return;
    }
    const prev = this.siteRtspRecording;
    this.siteRtspRecording = !this.siteRtspRecording;
    const a: Subscription = this.siteService
      .siteRtspRecordingChange(this.siteData._id, { is_rtsp_recording: this.siteRtspRecording })
      .subscribe({
        next: () => {
          // Success toast intentionally suppressed for toggle switches.
          this.siteData.is_rtsp_recording = this.siteRtspRecording;
          this.camera.forEach((camera) => {
            // Only cameras linked to a Live URL record; the API applies the same rule.
            camera.is_rtsp_recording = this.siteRtspRecording && this.hasLiveUrl(camera);
          });
          this.emitRecordingSummary();
          // The dashboard's camera list carries this switch (it decides whether an alert offers
          // Playback), and that list is cached for the session — so drop it rather than leave
          // the operator a Playback button for a camera that is no longer being recorded.
          this.rtspCameraService.clearCache();
        },
        error: (e) => {
          this.siteRtspRecording = prev;
          this.utilService.showErrorMessage('Something went wrong. please try again.');
          console.log(e);
        },
        complete: () => a.unsubscribe()
      });
  }

  private emitRecordingSummary() {
    const siteId = this.siteData?._id || this.cameraSiteID;
    if (!siteId) {
      return;
    }
    this.siteService.siteRecordingSummary$.next({
      siteId,
      cameras_recording: this.siteRtspRecording
        ? this.camera.filter((camera) => camera.is_rtsp_recording === true).length
        : 0,
      cameras_total: this.camera.length,
    });
  }

  toggleCameraRtspRecording(item: any) {
    if (!this.siteRtspRecording || !this.hasLiveUrl(item)) {
      return;
    }
    const prev = !!item.is_rtsp_recording;
    item.is_rtsp_recording = !item.is_rtsp_recording;
    const a: Subscription = this.siteService
      .cameraRtspRecordingChange(item._id, { is_rtsp_recording: item.is_rtsp_recording })
      .subscribe({
        next: () => {
          // Success toast intentionally suppressed for toggle switches.
          this.rtspCameraService.clearCache();
          this.emitRecordingSummary();
        },
        error: (e) => {
          item.is_rtsp_recording = prev;
          this.utilService.showErrorMessage('Something went wrong. please try again.');
          console.log(e);
        },
        complete: () => a.unsubscribe()
      });
  }

  // ---- The card's priority badge ----
  // Read from the same options list the lists above use, rather than a second map here:
  // one place decides which arrow and which word mean "high".

  priorityIcon(priority: unknown): string {
    return getAlertPriorityIcon(priority);
  }

  priorityLabel(priority: unknown): string {
    const level = toAlertPriority(priority);
    return level ? this.priorityOptions.find((option) => option.value === level)!.label : '';
  }

  // ---- Alert priority lists ----

  /** The value the server joins a window's cameras on — never `display_name`. */
  private cameraJoinName(item: any): string {
    return `${item?.name ?? item?.display_name ?? ''}`.trim();
  }

  private matchName(value: unknown): string {
    return `${value ?? ''}`.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  /** The names in one level's list, in the form a window stores them. */
  private levelCameraNames(level: AlertPriority): string[] {
    return (this.priorityBuckets[level] || [])
      .map((id) => this.cameraJoinName((this.camera as any[]).find((item) => item?._id === id)))
      .filter(Boolean);
  }

  /**
   * Re-derive every list. A level's cameras come from its window when it has one, and from
   * the cameras' own `alert_priority` when it does not.
   *
   * Two sources because a camera carries ONE priority: it can be at only one level that
   * runs all day, so a level with hours has to keep its own list on the window. That is
   * what lets the same camera be High for two hours and Medium for two others.
   */
  private rebuildPriorityBuckets(): void {
    const buckets: Record<string, string[]> = { high: [], medium: [], low: [] };
    const byName = new Map<string, any>();
    (this.camera as any[]).forEach((item) => {
      const key = this.matchName(this.cameraJoinName(item));
      if (key && item?._id) {
        byName.set(key, item);
      }
    });

    this.priorityOptions.forEach((option) => {
      const window = this.priorityWindows[option.value];
      if (!window) {
        return;
      }
      window.cameras.forEach((name) => {
        const item = byName.get(this.matchName(name));
        if (item && !buckets[option.value].includes(item._id)) {
          buckets[option.value].push(item._id);
        }
      });
    });

    // The camera's own level, which only speaks for a level that has no hours. A camera
    // still holding `alert_priority: high` after High was given hours is a leftover the
    // next save clears; showing it here would put the camera in a list it is not in.
    (this.camera as any[]).forEach((item) => {
      const level = toAlertPriority(item?.alert_priority);
      if (item?._id && level && !this.priorityWindows[level] && !buckets[level].includes(item._id)) {
        buckets[level].push(item._id);
      }
    });

    this.priorityBuckets = buckets;
    this.bucketSnapshot = {
      high: [...buckets['high']],
      medium: [...buckets['medium']],
      low: [...buckets['low']]
    };
    this.refreshPriorityOptions();
  }

  /**
   * What each list may offer: every camera, to all three levels.
   *
   * The lists used to hide whatever the other two held, one camera meaning one level. They
   * no longer do — a camera may be High for one stretch of the day and Medium for another,
   * and hiding it was what made the second level's dropdown read "No results found".
   * Overlapping HOURS are what is refused now, at save, in one place.
   *
   * Still recomputed into a field rather than read from a getter - a getter would hand the
   * multiselect a new array on every change-detection pass, which both re-renders the open
   * panel and is the usual source of NG0100 in this drawer.
   */
  private refreshPriorityOptions(): void {
    const options: Record<string, any[]> = {};
    const all = (this.camera as any[]).filter((item) => !!item?._id);
    this.priorityOptions.forEach((level) => {
      options[level.value] = all;
    });
    this.priorityCameraOptions = options;
  }

  /**
   * The level a camera sits at all day, or null.
   *
   * Only a level WITHOUT hours can be that, so this is what gets written to the camera
   * itself. A camera in two such levels is a contradiction — "always High" and "always
   * Medium" cannot both be true — and is refused before any of this runs.
   */
  private baseLevelFor(cameraId: string, buckets: Record<string, string[]>): AlertPriority | null {
    const match = this.priorityOptions.find(
      (option) => !this.priorityWindows[option.value] && (buckets[option.value] || []).includes(cameraId)
    );
    return match ? (match.value as AlertPriority) : null;
  }

  /**
   * Another level this camera already sits at all day, or null — the one combination the
   * lists cannot express, since the camera has a single `alert_priority` to hold it in.
   */
  private conflictingOpenLevel(cameraId: string, ignoreLevel: AlertPriority): string | null {
    const match = this.priorityOptions.find(
      (option) =>
        option.value !== ignoreLevel &&
        !this.priorityWindows[option.value] &&
        (this.priorityBuckets[option.value] || []).includes(cameraId)
    );
    return match ? match.label : null;
  }

  /**
   * A list changed.
   *
   * Where that is written depends on whether the level has hours. A level WITH hours keeps
   * its cameras on its window, so the change is a window save; a level without hours is
   * still the camera's own `alert_priority`, written per camera as it always was.
   */
  onPriorityBucketChange(level: AlertPriority): void {
    const current = [...(this.priorityBuckets[level] || [])];
    const before = this.bucketSnapshot[level] || [];

    // "Always High" and "always Medium" cannot both be true of one camera, so a camera may
    // sit in at most one level that has no hours. Give one of them hours and both become
    // possible, which is the whole point of the windows.
    const clash = current
      .filter((id) => !before.includes(id))
      .map((id) => ({ id, other: this.conflictingOpenLevel(id, level) }))
      .find((row) => !!row.other);
    if (clash && !this.priorityWindows[level]) {
      const name = (this.camera as any[]).find((item) => item?._id === clash.id);
      this.utilService.showErrorMessage(
        `${this.cameraJoinName(name) || 'That camera'} is already at ${clash.other}, which runs all day. ` +
        `Give ${clash.other} or ${level} a time window first.`
      );
      this.priorityBuckets[level] = [...before];
      this.refreshPriorityOptions();
      return;
    }

    this.bucketSnapshot[level] = current;
    this.refreshPriorityOptions();

    if (this.priorityWindows[level]) {
      // The level's list lives on its window, so this is a window save.
      const names = current
        .map((id) => this.cameraJoinName((this.camera as any[]).find((item) => item?._id === id)))
        .filter(Boolean) as string[];

      // A level with hours never reads `camera.alert_priority`, so any camera still
      // naming this level is a leftover — most often one taken OUT of this very list,
      // whose level was written back when the level had no hours. Left alone it keeps
      // colouring that camera's alerts at a level the list no longer shows.
      const stale = (this.camera as any[])
        .filter((item) => item?._id && toAlertPriority(item.alert_priority) === level)
        .map((item) => ({ id: item._id as string, level: null }));

      this.persistWindows(
        {
          ...this.priorityWindows,
          [level]: { ...(this.priorityWindows[level] as PriorityScheduleRow), cameras: names }
        },
        stale
      );
      return;
    }

    const added = current.filter((id) => !before.includes(id));
    // Only what this list dropped: a camera that moved to another level is added there and
    // rebuilt from the cameras, never removed from here as a separate write.
    const removed = before.filter((id) => !current.includes(id));

    if (added.length === 0 && removed.length === 0) {
      return;
    }

    const targets = (this.camera as any[]).filter(
      (item) => added.includes(item?._id) || removed.includes(item?._id)
    );
    const previous = new Map<any, AlertPriority | null>();
    targets.forEach((item) => {
      previous.set(item, toAlertPriority(item.alert_priority));
      item.alert_priority = added.includes(item._id) ? level : null;
    });
    this.isApplyingPriority = true;

    const a: Subscription = forkJoin(
      targets.map((item) =>
        this.siteService
          .cameraAlertPriorityChange(item._id, { alert_priority: item.alert_priority })
          .pipe(
            map(() => ({ item, ok: true })),
            catchError((e) => {
              console.log(e);
              return of({ item, ok: false });
            })
          )
      )
    ).subscribe({
      next: (results) => {
        const failed = results.filter((row) => !row.ok);
        failed.forEach((row) => {
          row.item.alert_priority = previous.get(row.item);
        });
        if (failed.length > 0) {
          this.utilService.showErrorMessage(
            failed.length === results.length
              ? 'Something went wrong. please try again.'
              : failed.length + ' of ' + results.length + ' cameras could not be updated.'
          );
        }
        this.isApplyingPriority = false;
        this.rebuildPriorityBuckets();
        this.cdr.markForCheck();
      },
      error: (e) => {
        targets.forEach((item) => {
          item.alert_priority = previous.get(item);
        });
        this.isApplyingPriority = false;
        this.utilService.showErrorMessage('Something went wrong. please try again.');
        console.log(e);
        this.rebuildPriorityBuckets();
        this.cdr.markForCheck();
      },
      complete: () => a.unsubscribe()
    });
  }

  // ---- Time-based priority: the site's recurring windows ----

  /**
   * Moves cameras between "held by a level's window" and "held by their own level".
   *
   * One request per camera, like the priority lists above — the API has no bulk endpoint —
   * and each carries its own catchError so one failure does not abandon the rest. The
   * lists are rebuilt from whatever actually saved, so a partial result shows as itself
   * rather than as the change the operator asked for.
   */
  private applyCameraFixups(fixups: Array<{ id: string; level: AlertPriority | null }>): void {
    const targets = fixups
      .map((fix) => ({ item: (this.camera as any[]).find((row) => row?._id === fix.id), level: fix.level }))
      .filter((fix) => !!fix.item && toAlertPriority(fix.item.alert_priority) !== fix.level);
    if (!targets.length) {
      this.rebuildPriorityBuckets();
      return;
    }

    this.isApplyingPriority = true;
    const a: Subscription = forkJoin(
      targets.map((fix) =>
        this.siteService.cameraAlertPriorityChange(fix.item._id, { alert_priority: fix.level }).pipe(
          map(() => {
            fix.item.alert_priority = fix.level;
            return true;
          }),
          catchError((e) => {
            console.log(e);
            return of(false);
          })
        )
      )
    ).subscribe({
      next: (results) => {
        const failed = results.filter((ok) => !ok).length;
        if (failed) {
          this.utilService.showErrorMessage(
            `${failed} of ${results.length} cameras could not be moved. Reopen the panel to see what saved.`
          );
        }
        this.isApplyingPriority = false;
        this.rebuildPriorityBuckets();
        this.cdr.markForCheck();
      },
      error: (e) => {
        this.isApplyingPriority = false;
        console.log(e);
        this.rebuildPriorityBuckets();
        this.cdr.markForCheck();
      },
      complete: () => a.unsubscribe()
    });
  }

  /**
   * The site's windows, keyed by the level each one hangs off.
   *
   * Silent on failure: this is one block of a drawer that does several other things, and
   * an install whose API predates the endpoint must still be able to set a camera's
   * priority the old way.
   */
  private loadPriorityScheduleRows(): void {
    if (!this.cameraSiteID) {
      return;
    }
    this.priorityScheduleLoading = true;
    const a: Subscription = this.siteService.getSiteAlertPrioritySchedules(this.cameraSiteID).subscribe({
      next: (res: any) => {
        const rows = Array.isArray(res?.alert_priority_schedules) ? res.alert_priority_schedules : [];
        this.priorityWindows = { high: null, medium: null, low: null };
        // First wins per level. The server can hold several windows for one level — the
        // drawer has one slot each, so the extras are left stored and simply not shown
        // rather than being silently deleted by the next save from here.
        rows.map(toPriorityScheduleRow).forEach((row: PriorityScheduleRow) => {
          if (!this.priorityWindows[row.priority]) {
            this.priorityWindows[row.priority] = row;
          }
        });
        this.priorityScheduleLoading = false;
        // The lists are derived from these, so they cannot be built until the windows are
        // in: the pass ngOnInit ran saw no windows and read every level off the cameras.
        this.rebuildPriorityBuckets();
        this.cdr.markForCheck();
      },
      error: (e) => {
        this.priorityScheduleLoading = false;
        console.log(e);
      },
      complete: () => a.unsubscribe()
    });
  }

  /** Opens the editor under one level, on its window or a fresh one. */
  openWindowEditor(level: AlertPriority): void {
    const existing = this.priorityWindows[level];
    this.windowDraft = existing ? { ...existing } : emptyPriorityScheduleRow(level);
    // Seeded from the level's list, because that is what will be saved onto the window —
    // and because the overlap check reads it: two levels MAY share hours when they hold
    // different cameras, and a draft with an empty list claims the whole site.
    this.windowDraft.cameras = this.levelCameraNames(level);
    this.windowDraftStart = windowTimeToDate(this.windowDraft.startTime);
    this.windowDraftEnd = windowTimeToDate(this.windowDraft.endTime);
    this.windowEditorLevel = level;
    this.windowDraftError = null;
  }

  closeWindowEditor(): void {
    this.windowEditorLevel = null;
    this.windowDraft = null;
    this.windowDraftStart = null;
    this.windowDraftEnd = null;
    this.windowDraftError = null;
  }

  /** Pulls the pickers back onto the draft, so the "+1 day" hint tracks what they show. */
  onWindowTimeChange(): void {
    if (!this.windowDraft) {
      return;
    }
    this.windowDraft.startTime = dateToWindowTime(this.windowDraftStart);
    this.windowDraft.endTime = dateToWindowTime(this.windowDraftEnd);
    this.windowDraftError = null;
  }

  isWindowEditorOpen(level: AlertPriority): boolean {
    return this.windowEditorLevel === level;
  }

  windowSummary(level: AlertPriority): string {
    const row = this.priorityWindows[level];
    return row ? formatWindowRange(row) : '';
  }

  windowCrossesMidnight(row: PriorityScheduleRow | null): boolean {
    return !!row && crossesMidnight(row);
  }

  /**
   * The clash with another level, live as the pickers move.
   *
   * A getter rather than a field set in `onWindowTimeChange`, because it also has to
   * answer the moment the editor opens on an existing window. It returns a string built
   * from the draft, not a new object, so it is safe to read from the template.
   */
  get windowDraftOverlap(): string | null {
    return this.windowDraft ? windowOverlapError(this.windowDraft, this.priorityWindows) : null;
  }

  /** Applies the open draft to its level and writes every window in one request. */
  saveWindowEditor(): void {
    if (!this.windowDraft || !this.windowEditorLevel) {
      return;
    }
    // Read the pickers here too, not only on change: a picker closed by clicking Save
    // directly can emit its last value after the click, so the draft is refreshed from
    // them rather than trusted to already be current.
    this.onWindowTimeChange();
    // A camera may sit at two levels but not at the same moment, so a window may not
    // overlap another level's. The server refuses this too and is the gate that counts;
    // checking here is what turns a toast after a round trip into a message under the
    // field the operator is already looking at.
    const error =
      priorityScheduleRowError(this.windowDraft) ||
      windowOverlapError(this.windowDraft, this.priorityWindows);
    if (error) {
      this.windowDraftError = error;
      return;
    }
    // The level's current cameras carry over onto the window it is being given: the list
    // the operator is looking at must not empty itself the moment they set hours on it.
    // Their `alert_priority` is then cleared, because the window is now what holds them —
    // leaving it set would keep them at this level around the clock, which is the bug the
    // hours were added to fix.
    const level = this.windowEditorLevel;
    const ids = [...(this.priorityBuckets[level] || [])];

    this.persistWindows(
      { ...this.priorityWindows, [level]: { ...this.windowDraft } },
      ids.map((id) => ({ id, level: null }))
    );
  }

  removeWindow(level: AlertPriority): void {
    const window = this.priorityWindows[level];
    if (!window) {
      return;
    }
    // Dropping the hours means this level runs all day again, so its cameras go back onto
    // their own `alert_priority` — otherwise the list would simply empty and the operator
    // would have lost an assignment they never touched.
    //
    // Except any camera that already sits at another all-day level: a camera holds one
    // priority, so that one keeps what it has rather than being overwritten from here.
    const ids = (this.camera as any[])
      .filter((item) => window.cameras.some((name) => this.matchName(name) === this.matchName(this.cameraJoinName(item))))
      .filter((item) => !this.conflictingOpenLevel(item._id, level))
      .map((item) => item._id);

    this.persistWindows(
      { ...this.priorityWindows, [level]: null },
      ids.map((id) => ({ id, level }))
    );
  }

  /**
   * Writes the three levels' windows as one list.
   *
   * The API replaces the site's rules with whatever arrives, so a removed window is a
   * window simply left out — there is no delete call. The previous map is restored on
   * failure, because the summary beside a level is the only thing telling the operator
   * whether their change took.
   */
  private persistWindows(
    next: Record<string, PriorityScheduleRow | null>,
    cameraFixups: Array<{ id: string; level: AlertPriority | null }> = []
  ): void {
    if (!this.cameraSiteID || this.priorityScheduleSaving) {
      return;
    }
    const previous = this.priorityWindows;
    this.priorityWindows = next;
    this.priorityScheduleSaving = true;

    const payload = this.priorityOptions
      .map((option) => next[option.value])
      .filter((row): row is PriorityScheduleRow => !!row)
      .map(toPrioritySchedulePayload);

    const a: Subscription = this.siteService
      .saveSiteAlertPrioritySchedules(this.cameraSiteID, payload)
      .subscribe({
        next: (res: any) => {
          const rows = Array.isArray(res?.alert_priority_schedules) ? res.alert_priority_schedules : [];
          // Re-read from the response rather than keeping the local rows: the server
          // normalises the times and mints ids for new rows, and a stale id here would
          // create a duplicate on the next save instead of updating.
          const saved: Record<string, PriorityScheduleRow | null> = { high: null, medium: null, low: null };
          rows.map(toPriorityScheduleRow).forEach((row: PriorityScheduleRow) => {
            if (!saved[row.priority]) {
              saved[row.priority] = row;
            }
          });
          this.priorityWindows = saved;
          this.priorityScheduleSaving = false;
          this.closeWindowEditor();
          this.utilService.showErrorSuccess(res?.message || 'Time-based alert priority saved');
          // Only after the windows are stored: these move cameras between "held by a
          // window" and "held by their own level", and doing it first would leave a camera
          // belonging to neither if the window save then failed.
          this.applyCameraFixups(cameraFixups);
          this.cdr.markForCheck();
        },
        error: (e) => {
          this.priorityWindows = previous;
          this.priorityScheduleSaving = false;
          console.log(e);
          this.cdr.markForCheck();
        },
        complete: () => a.unsubscribe()
      });
  }

  // ---- Camera-level object detection (person + vehicle + zone) ----
  // Effective detection = site flag AND camera flag. A camera toggle has no
  // effect while its site flag is off, so we disable it with a hint tooltip.
  cameraDetectionTooltip(): string {
    return 'Enable at site level first';
  }

  // Person + Vehicle are persisted together in one call — always send both
  // current values so neither field gets cleared by the update.
  private persistCameraObjectDetection(item: any, prev: { face: boolean; vehicle: boolean }) {
    const payload = {
      camera: item._id,
      is_face_detection: item.is_face_detection ? true : false,
      is_vehicle_detection: item.is_vehicle_detection ? true : false
    };
    const a: Subscription = this.siteService.changeCameraFaceDetection(payload).subscribe({
      next: (res) => {
        // Success toast intentionally suppressed for camera detection toggles.
        if (!(res && res.status)) {
          item.is_face_detection = prev.face;
          item.is_vehicle_detection = prev.vehicle;
          this.utilService.showErrorMessage('Something went wrong. please try again.');
        }
      },
      error: (e) => {
        // revert optimistic update on failure
        item.is_face_detection = prev.face;
        item.is_vehicle_detection = prev.vehicle;
        this.utilService.showErrorMessage('Something went wrong. please try again.');
        console.log(e);
      },
      complete: () => a.unsubscribe()
    });
  }

  toggleCameraFaceDetection(item: any) {
    if (!this.siteFaceDetection) {
      return;
    }
    const prev = { face: !!item.is_face_detection, vehicle: !!item.is_vehicle_detection };
    item.is_face_detection = !item.is_face_detection;
    this.persistCameraObjectDetection(item, prev);
  }

  toggleCameraVehicleDetection(item: any) {
    if (!this.siteVehicleDetection) {
      return;
    }
    const prev = { face: !!item.is_face_detection, vehicle: !!item.is_vehicle_detection };
    item.is_vehicle_detection = !item.is_vehicle_detection;
    this.persistCameraObjectDetection(item, prev);
  }

  toggleCameraZoneDetection(item: any) {
    if (!this.siteZoneDetection) {
      return;
    }
    const prev = !!item.is_zone_detection;
    item.is_zone_detection = !item.is_zone_detection;
    const a: Subscription = this.siteService
      .cameraZoneActiveStatusChange(item._id, { is_zone_detection: item.is_zone_detection })
      .subscribe({
        next: () => {
          // Success toast intentionally suppressed for camera zone toggle.
        },
        error: (e) => {
          item.is_zone_detection = prev;
          this.utilService.showErrorMessage('Something went wrong. please try again.');
          console.log(e);
        },
        complete: () => a.unsubscribe()
      });
  }

  // Per-camera alert active. Independent of the site-level flag above, but a
  // camera can't raise alerts while the site itself is inactive.
  toggleCameraAlert(item: any) {
    const prev = !!item.is_alert_active;
    item.is_alert_active = !item.is_alert_active;
    this.changeCameraStatus(item, prev);
  }

  toggleCameraArchive(item: any) {
    item.is_archive_alert_active = !item.is_archive_alert_active;
    this.changeCameraArchiveAlertStatus(item);
  }

  changeCameraArchiveAlertStatus(row: any) {
    const updateSiteStatus = this.siteService.cameraAlertArchiveEnableDisable(row._id, { is_archive_alert_active: row.is_archive_alert_active }).subscribe({
      next: (res) => {
        this.utilService.showErrorSuccess(res.message);
      },
      error: (e) => console.log(e),
      complete: () => updateSiteStatus.unsubscribe()
    });
  }

  closeModal() {
    const reason = !this.isAnythingChanged ? 'Changes made' : 'No changes';
    if (this.embedded) {
      this.siteService.closeCameraDrawer(reason);
      return;
    }
    this.modalService.setDismissReason(reason);
    this.modalService.hide();
  }

  onImageError(index: number) {
    this.camera[index].alert_image = null;
  }

  goToSpeakerList(camera_id: string) {
    this.closeModal();
    this.router.navigate([`sites/camera-speaker/${camera_id}`]);
  }

  // Camera Location Dropdown Methods


  fetchDropdownOptions() {
    this.alertService.getAlertLocationDropdownOptions().subscribe({
      next: (res) => {
        if (res && res.status && Array.isArray(res.data)) {
          this.cameraLocationOptions = res.data.map((item: any) => ({
            id: item.labelName,
            _id: item._id,
            label: item.labelName,
            color: item.color || '#FFD700',
            textColor: item.text_color || DEFAULT_LOCATION_TEXT_COLOR,
            isCustom: true
          }));



          if (this.cameraLocationOptions.length > 0) {
            this.selectedCameraLocation = this.cameraLocationOptions[0].id;
            this.camera.forEach(camera => {
              camera.selectedCameraLocation = camera.camera_location || '';
            });
          }
        }
      },
      error: (e) => {
        // Handle error silently - no default options
      }
    });
  }

  onCameraLocationChange(item: any, locationId: string | { id: string; label: string; color: string; isCustom: boolean }) {
    if (typeof locationId === 'string') {
      item.selectedCameraLocation = locationId;
    } else if (locationId && locationId.id) {
      item.selectedCameraLocation = locationId.id;
    }
    this.updateCameraLocation(item);
  }

  updateCameraLocation(item: any) {
    const option = this.cameraLocationOptions.find((o) => o.id === item.selectedCameraLocation);
    const color = option?.color;
    // Both colours, always together: the API copies them onto the camera and onto every
    // alert it has already raised, and a fill that arrives without its text colour is the
    // one pairing the queue cannot draw.
    const textColor = option?.textColor || DEFAULT_LOCATION_TEXT_COLOR;
    this.siteService.updateCameraLocation(item._id, color, textColor, this.cameraSiteID, item.selectedCameraLocation).subscribe({
      next: (res: any) => {
        if (res && res.status) {
          this.utilService.showErrorSuccess('Camera location updated successfully!');
        } else {
          this.utilService.showErrorMessage('Failed to update camera location. Please try again.');
        }
      },
      error: (e) => {
        this.utilService.showErrorMessage('Failed to update camera location. Please try again.');
      }
    });
  }

  openZoomModal(item: any) {
    if (!this.canEditCameraOrZone) {
      return;
    }
    // zone-create reads the camera item from cameraSiteID (existing contract).
    this.siteService.cameraSiteID = item;
    this.siteService.openZoneDrawer();
  }

  private checkUserPermissions() {
    this.loginUserRole = this.utilService.getLocalStorage(localStorageKeys.roleType);
    this.canManageDropdown = [
      userRoleNameEnum.superAdmin,
      userRoleNameEnum.clientSupport,
      userRoleNameEnum.IT
    ].includes(this.loginUserRole as userRoleNameEnum);
    this.canEditCameraOrZone = [
      userRoleNameEnum.superAdmin,
      userRoleNameEnum.clientSupport,
      userRoleNameEnum.IT,
      userRoleNameEnum.manager
    ].includes(this.loginUserRole as userRoleNameEnum);
  }
}
